import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Marker asset of the humann-core-engine extension, loaded on every page by the
 * cart-cleanup app embed. Its directory is the extension directory, so it gives
 * us the offer module's asset base without hardcoding a handle or version —
 * both of which change on deploy (`product-tiers-599` → `cart-cleanup-v2.9`).
 */
const OFFER_MODULE_MARKER_ASSET = '/assets/cart-cleanup.js';

/** Offer-module assets to adopt, relative to the extension directory. */
const OFFER_MODULE_ASSETS = ['app.css', 'app.js'];

/**
 * Copies the offer-module extension's app.css/app.js out of a fetched PDP.
 *
 * The zephyr-* templates render the offer module as the humann-core-engine app
 * block, which — unlike the theme block — inlines only its data, and Shopify
 * links its CSS and OfferModule class from the PDP head alone. Scoped to this
 * extension so Bazaarvoice/Firework/Aspire don't follow it onto collection pages.
 *
 * @param {Document} html - The fetched product page
 * @returns {Promise<unknown>} Resolves once the adopted assets have loaded
 */
function adoptOfferModuleAppAssets(html) {
  // The marker is in the current document (app embed) — the fetched PDP would
  // give the same directory, but reading it locally keeps this a cheap lookup.
  const marker = document.querySelector(`script[src*="${OFFER_MODULE_MARKER_ASSET}"]`);
  const markerSrc = marker?.getAttribute('src');
  if (!markerSrc) return Promise.resolve();

  const base = markerSrc.slice(0, markerSrc.indexOf(OFFER_MODULE_MARKER_ASSET) + '/assets/'.length);
  const selector = OFFER_MODULE_ASSETS.map(
    (asset) => `link[rel="stylesheet"][href^="${base}${asset}"], script[src^="${base}${asset}"]`
  ).join(', ');

  const pending = [];

  for (const tag of html.head.querySelectorAll(selector)) {
    const url = tag.getAttribute('href') || tag.getAttribute('src');
    if (!url || document.querySelector(`link[href="${url}"], script[src="${url}"]`)) continue;

    const adopted = document.createElement(tag.tagName.toLowerCase());
    for (const attr of tag.attributes) adopted.setAttribute(attr.name, attr.value);

    pending.push(
      new Promise((resolve) => {
        adopted.addEventListener('load', resolve, { once: true });
        adopted.addEventListener('error', resolve, { once: true });
      })
    );

    document.head.appendChild(adopted);
  }

  return Promise.all(pending);
}

/**
 * Re-executes inline scripts inside `#quick-add-modal-content`.
 *
 * After morph(), inline <script> tags exist in the DOM but the browser does
 * not run them, so we must recreate them. The Offer Module ships inline
 * scripts that declare top-level `const`/`class` globals (subOptionsDisplay,
 * productObject, tiered_discounts, class OfferModule, …). Recreating each
 * script verbatim works on the FIRST open but throws "Identifier already
 * declared" on every subsequent open — which kills the script before
 * initOfferModules() runs, so options/radios/Add-to-Cart stop working.
 *
 * To make it idempotent we run all classic inline scripts together inside a
 * single IIFE: their declarations become function-scoped and never collide
 * across opens. External (src) and module scripts are recreated individually;
 * non-JS blocks (e.g. application/json data) are left untouched.
 *
 * morph() can also reuse offer-module DOM nodes, keeping stale event
 * listeners from a previous open. We clone-replace each offer module first to
 * drop those listeners (and clear its initialized flag) so re-init binds
 * exactly one set of handlers.
 */
function reExecuteModalInlineScripts() {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;

  // Drop stale listeners on reused offer-module nodes and allow re-init.
  for (const offerModule of modalContent.querySelectorAll('[data-offer-module-basic-id]')) {
    const fresh = /** @type {Element} */ (offerModule.cloneNode(true));
    fresh.removeAttribute('data-offer-module-initialized');
    offerModule.replaceWith(fresh);
  }

  const scripts = modalContent.querySelectorAll('script');
  const inlineChunks = [];

  for (const oldScript of scripts) {
    const type = (oldScript.getAttribute('type') || '').toLowerCase();

    if (oldScript.src) {
      // External script — recreate so it loads.
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
      oldScript.replaceWith(newScript);
      continue;
    }

    if (type === 'module') {
      const newScript = document.createElement('script');
      newScript.type = 'module';
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
      continue;
    }

    if (type && type !== 'text/javascript' && type !== 'application/javascript') {
      // Non-JS data block (e.g. application/json) — leave as-is, don't run.
      continue;
    }

    // Classic inline JS — collect to run once inside a shared IIFE scope.
    inlineChunks.push(oldScript.textContent || '');
    oldScript.remove();
  }

  if (inlineChunks.length) {
    const combined = document.createElement('script');
    combined.textContent = `(function(){\n${inlineChunks.join('\n;\n')}\n})();`;
    modalContent.appendChild(combined);
  }

  // The app block has no inline logic to re-run; app.js exposes its init as a
  // global instead. Undefined on the theme-block path, which self-inits above.
  const initAppOfferModules = /** @type {(() => void) | undefined} */ (
    /** @type {any} */ (window).initOfferModules
  );
  initAppOfferModules?.();
}

/**
 * Strips the media gallery's image-zoom feature from fetched product content.
 *
 * The drawer renders a clone of the PDP's `[data-product-grid-content]`, so it
 * inherits every zoom affordance the gallery ships: the `<zoom-dialog>` markup
 * and its module, a full-bleed overlay button on each grid image
 * (`on:click="/zoom/<i>"`), and a `#zoom-dialog-<block_id>/open/<i>` handler on
 * each slideshow slide. Zoom belongs to the PDP only — the drawer shows a plain
 * horizontal strip — so remove it before the content is cached and morphed in.
 *
 * Dropping the `on:*` attribute is enough to unbind the behavior: assets/component.js
 * delegates from document and reads the attribute at dispatch time.
 *
 * @param {Element} content - The fetched product grid content
 */
function stripZoomFeature(content) {
  const removable = content.querySelectorAll(
    'zoom-dialog, .product-media-container__zoom-button, script[src*="zoom-dialog"], script[src*="drag-zoom-wrapper"]'
  );
  for (const element of removable) element.remove();

  // Slideshow slides open the zoom dialog directly — no overlay button to remove.
  for (const slide of content.querySelectorAll('[on\\:click*="zoom-dialog-"]')) {
    slide.removeAttribute('on:click');
  }

  // Drops the `cursor: zoom-in` affordance (product-media-gallery-content-styles).
  for (const media of content.querySelectorAll('.product-media-container--zoomable')) {
    media.classList.remove('product-media-container--zoomable');
  }
}

/**
 * Fetches a product page and returns the parsed Document.
 * @param {string} url - The product page URL
 * @param {AbortSignal} [signal] - Optional abort signal
 * @returns {Promise<Document | null>}
 */
async function fetchProductPage(url, signal) {
  if (!url) return null;

  try {
    const response = await fetch(url, { signal });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    return new DOMParser().parseFromString(responseText, 'text/html');
  } catch (error) {
    if (error.name === 'AbortError') return null;
    throw error;
  }
}

/**
 * Builds a Bazaarvoice inline-rating gate for the quick-view drawer header.
 *
 * Per Figma the header shows the star rating + "4.3 (7,128)" above the product
 * title. The gate goes in empty and BV fills it: its script watches the whole
 * document for `[data-bv-show]`, so a gate injected into the morphed drawer is
 * picked up like any server-rendered one. Reading BV directly is what keeps the
 * drawer agreeing with the PDP — the earlier version copied the card's
 * metafield-rendered stars, which is the mismatch HUM-1026 reported.
 *
 * Module scope, not a method: both QuickAddComponent and QuickAddDialog build
 * this header.
 *
 * @param {string | undefined} productId
 * @returns {string}
 */
function buildHeaderRatingHtml(productId) {
  if (!productId) return '';

  return (
    `<div class="quick-add-modal__header-rating bv-inline-rating"` +
    ` data-bv-show="inline_rating" data-bv-product-id="${productId}" data-bv-seo="false"></div>`
  );
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );
    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest('product-hotspot-component')
      );
    const productLink =
      productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(
      ThemeEvents.variantSelected,
      this.#updateQuickAddButtonState.bind(this)
    );
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (
      modalContent.querySelector('variant-picker')
    );
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    // Open the drawer immediately on the first click so it always responds
    // right away. When the content is not cached yet, the fetch + render
    // happens afterwards behind a loading state instead of blocking the open.
    const headerInfo = document.querySelector('#quick-add-dialog [ref="headerInfo"]');
    if (headerInfo) headerInfo.innerHTML = '';

    this.#openQuickAddModal();

    if (!productGrid) {
      this.#setModalLoading(true);
      this.setAttribute('aria-busy', 'true');
      try {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          await adoptOfferModuleAppAssets(html);

          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            stripZoomFeature(productGrid);
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);

      // Populate drawer header with rating + product title
      this.#populateDrawerHeader();

      // Update View Full Details link
      this.#updateViewFullDetailsLink();

      // Re-execute inline scripts (critical for Offer Module initialization)
      reExecuteModalInlineScripts();
    }

    this.#setModalLoading(false);
  };

  /**
   * Toggles the loading state on the drawer while content is being fetched.
   * @param {boolean} isLoading
   */
  #setModalLoading(isLoading) {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.toggleAttribute('loading', isLoading);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    // The modal is global markup, so the trigger's attribution cannot be reached by
    // walking up from the injected product form — carry it onto the dialog on the way in.
    for (const name of ['productOrigin', 'offerKey', 'offerValue']) {
      const value = this.dataset[name]?.trim();
      if (value) dialogComponent.dataset[name] = value;
      else delete dialogComponent.dataset[name];
    }

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the quick-add drawer content.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Populates the drawer header with the product rating and title.
   */
  #populateDrawerHeader() {
    const headerInfo = document.querySelector('#quick-add-dialog [ref="headerInfo"]');
    if (!headerInfo) return;

    const productTitle = this.dataset.productTitle || '';
    const ratingHtml = buildHeaderRatingHtml(this.dataset.productId);

    headerInfo.innerHTML = `${ratingHtml}<div class="quick-add-modal__header-title">${productTitle}</div>`;
  }

  /**
   * Updates the View Full Details link href in the drawer
   */
  #updateViewFullDetailsLink() {
    if (!this.productPageUrl) return;

    // The "View Full Details" link is a stable footer element in the drawer.
    const footerLink = document.querySelector('#quick-add-dialog [ref="viewDetailsLink"]');
    if (footerLink instanceof HTMLAnchorElement) {
      footerLink.href = this.productPageUrl;
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId === selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #flavourFetchAbort = null;

  connectedCallback() {
    super.connectedCallback();

    // Listen on document, not on the dialog: the Offer Module dispatches
    // cart:update on document (it does not bubble down to the dialog), so a
    // dialog-scoped listener would miss it and the quick-view drawer would stay
    // open in front of the cart drawer after an Offer Module add-to-cart.
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Intercept flavour swatch clicks inside the quick-add drawer so they
    // update the drawer content in-place instead of navigating to the PDP.
    this.addEventListener('click', this.#handleFlavourSwatchClick, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#flavourFetchAbort?.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = async (event) => {
    // Only act when the add happened from within the quick-view drawer.
    if (!this.refs.dialog?.open) return;
    if (event.detail?.data?.didError) return;

    // Fully close the quick-view drawer first so its scroll-lock (body
    // position:fixed) is released, THEN open the cart drawer. Doing both on the
    // same event races — the cart drawer reads scrollY while the body is still
    // fixed and sometimes fails to open. Sequencing makes it deterministic.
    await this.closeDialog();

    const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (cartDrawer && typeof cartDrawer.open === 'function' && !cartDrawer.refs?.dialog?.open) {
      cartDrawer.open();
    }
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.view-product-title a')
    );
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (
      this.querySelector('.product-header a')
    );

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  /**
   * Intercepts clicks on flavour swatch anchors inside the quick-add drawer.
   * Fetches the target product's page, morphs the drawer content, updates the
   * header and footer link, and re-executes inline scripts.
   *
   * Only fires inside the dialog — PDP swatch links remain normal anchors.
   *
   * @param {MouseEvent} event
   */
  #handleFlavourSwatchClick = async (event) => {
    const anchor = /** @type {HTMLElement | null} */ (event.target)?.closest?.(
      '.flavour-swatches__item'
    );
    if (!anchor || !(anchor instanceof HTMLAnchorElement)) return;

    // Skip selected or disabled swatches
    if (
      anchor.classList.contains('flavour-swatches__item--selected') ||
      anchor.classList.contains('flavour-swatches__item--disabled')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const productUrl = anchor.href;
    const productTitle = anchor.dataset.productTitle || '';

    if (!productUrl) return;

    // Abort any in-flight flavour fetch
    this.#flavourFetchAbort?.abort();
    this.#flavourFetchAbort = new AbortController();

    this.toggleAttribute('loading', true);

    try {
      const html = await fetchProductPage(productUrl, this.#flavourFetchAbort.signal);
      if (!html) return;

      await adoptOfferModuleAppAssets(html);

      const gridElement = html.querySelector('[data-product-grid-content]');
      const modalContent = document.getElementById('quick-add-modal-content');
      if (!gridElement || !modalContent) return;

      stripZoomFeature(gridElement);
      morph(modalContent, gridElement);

      // Update drawer header with product title (and rating if available). The
      // switched-to product's id comes off the fetched PDP, since `this.dataset`
      // still holds the card's original product.
      const headerInfo = this.querySelector('[ref="headerInfo"]');
      if (headerInfo) {
        const newProductId =
          html
            .querySelector('sticky-add-to-cart[data-product-id]')
            ?.getAttribute('data-product-id') || undefined;
        const ratingHtml = buildHeaderRatingHtml(newProductId);

        headerInfo.innerHTML = `${ratingHtml}<div class="quick-add-modal__header-title">${productTitle}</div>`;
      }

      // Update "View Full Details" link
      const viewDetailsLink = /** @type {HTMLAnchorElement | null} */ (
        this.querySelector('[ref="viewDetailsLink"]')
      );
      if (viewDetailsLink) {
        viewDetailsLink.href = productUrl;
      }

      // Re-execute inline scripts (critical for Offer Module initialization)
      reExecuteModalInlineScripts();

      // Scroll drawer body to top
      const drawerBody = this.querySelector('.quick-add-modal__body');
      if (drawerBody) {
        drawerBody.scrollTo({ top: 0, behavior: 'instant' });
      }
    } finally {
      this.toggleAttribute('loading', false);
      this.#flavourFetchAbort = null;
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4))
      return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
